<template>
  <div
    ref="root"
    class="input-container"
    :class="[
      newDesign && 'new-design',
      { 'is-keyboard-focus': focusedViaKeyboard },
    ]"
  >
    <slot name="before" />
    <div
      v-if="hasLabelSlot"
      :id="labelId"
      data-testid="input-label"
    >
      <slot name="label" />
    </div>

    <template v-else>
      <label
        v-if="label"
        :id="labelId"
        :class="
          twMerge(
            'tw:block!',
            newDesign && 'field-heading',
            !description && 'tw:mb-2',
            description && 'tw:mb-1',
            labelClass
          )
        "
        data-testid="input-label"
        :for="computedId"
      >
        {{ label }}
        <slot name="appendLabel" />
      </label>

      <div
        v-if="description"
        :id="descriptionId"
        class="field-subtext q-mb-sm"
      >
        {{ description }}
      </div>
    </template>
    <div
      class="input-wrapper"
      :class="{
        prepend: hasPrependSlot,
        append: hasAppendSlot || canClear,
        rounded: rounded,
        'not-filled': notFilled,
        dense: dense,
        borderless: borderless,
      }"
    >
      <slot name="prepend" />
      <div :class="computedWrapperClasses">
        <slot
          :class="inputClasses"
          :style="computedStyle"
        >
          <textarea
            v-if="type === 'textarea'"
            :id="computedId"
            ref="textbox"
            :class="inputClasses"
            :disabled="disabled"
            :placeholder="placeholder"
            :rows="rows"
            :style="computedStyle"
            :value="input"
            v-bind="{ ...inheritedAttributes, ...eventListeners }"
          />
          <input
            v-else
            :id="computedId"
            v-bind="{ ...inheritedAttributes, ...eventListeners }"
            ref="textbox"
            :class="inputClasses"
            :disabled="disabled"
            :inputmode="inputMode"
            :placeholder="placeholder"
            :style="computedStyle"
            :type="inputType"
            :value="input"
          />
        </slot>

        <slot
          :focused="focused"
          name="textboxAppend"
        />
      </div>
      <div
        v-if="clearable"
        class="clear"
      >
        <BaseButton
          v-if="canClear"
          :aria-label="getLocaleText('global.clear')"
          circle
          class="clear-icon"
          data-testid="input-clear-button"
          flat
          icon="close-filled"
          new-design
          new-icon
          size="sm"
          @click.stop="clear"
        />
      </div>
      <slot name="append" />
    </div>
    <slot name="after" />

    <slot
      :dirty="dirty"
      :errors="errors"
      name="errors"
    >
      <div
        v-if="errors.length"
        :id="errorId"
        class="field-error q-mt-sm"
        :data-testid="errorId"
        role="alert"
      >
        {{ errors[0] }}
      </div>
    </slot>
  </div>
</template>

<script lang="ts">
import {
  computed,
  type CSSProperties,
  type InputTypeHTMLAttribute,
  nextTick,
  onBeforeMount,
  onBeforeUpdate,
  onMounted,
  ref,
  toRef,
  useAttrs,
  useId,
  useSlots,
  watch,
} from "vue";

type TextareaInputType = "textarea";

type StringInputType = Extract<
  InputTypeHTMLAttribute,
  "text" | "number" | "password" | "search"
>;

type FileInputType = Extract<InputTypeHTMLAttribute, "file">;

export type InputType = StringInputType | FileInputType | TextareaInputType;
export type InputTextSubmitEvent = {
  event: KeyboardEvent;
  value: Nullable<string>;
};
</script>

<script lang="ts" setup generic="T extends InputType = 'text'">
import { debounce as lodashDebounce, omit } from "lodash-es";
import { twMerge } from "tailwind-merge";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButton } from "shared/components/base";
import useTabbing from "shared/composables/useTabbing";
import useValidation, {
  type Validations,
} from "shared/composables/useValidation";
import { hasSlotContent } from "shared/helpers/hasSlotContent";
import { inputNumberOnly } from "shared/helpers/number";
import { type Nullable } from "shared/types";

export interface InputTextProps<U = InputType> {
  ariaLabelledby?: string;
  autoFocus?: boolean;
  autoGrow?: boolean;
  borderless?: boolean;
  clearable?: boolean;
  debounce?: number | string;
  dense?: boolean;
  description?: string;
  disabled?: boolean;
  hasPrependContent?: boolean | null;
  height?: number | string;
  id?: string;
  inputClasses?: string | string[] | Record<string, boolean>;
  label?: string;
  labelClass?: string;
  maxHeight?: number;
  minHeight?: number;
  newDesign?: boolean;
  notFilled?: boolean;
  numbersOnly?: boolean;
  optional?: boolean;
  placeholder?: string;
  resize?: CSSProperties["resize"];
  rounded?: boolean;
  rows?: number;
  type?: U | InputType;
  validations?: Validations;
  wrapperClasses?: string | string[] | Record<string, boolean>;
}

type InputMode = "numeric" | undefined;

type ModelType = T extends FileInputType
  ? FileList | null
  : T extends StringInputType | TextareaInputType
    ? string | null
    : never;

const props = withDefaults(defineProps<InputTextProps<T>>(), {
  debounce: 0,
  ariaLabelledby: undefined,
  description: "",
  hasPrependContent: null,
  height: "",
  id: "",
  inputClasses: "",
  label: "",
  labelClass: "",
  maxHeight: 0,
  minHeight: 0,
  placeholder: "",
  resize: "none",
  rows: undefined,
  type: "text",
  validations: () => [],
  wrapperClasses: "",
});

const model = defineModel<ModelType>({
  default: undefined,
});

const emit = defineEmits<{
  blur: [];
  clear: [];
  focus: [];
  keydown: [KeyboardEvent];
  submit: [InputTextSubmitEvent];
}>();

const attrs = useAttrs();
const slots = useSlots();
const autoId = useId();

const computedId = computed(() => props.id || autoId);
const labelId = computed(() => `${computedId.value}-label`);
const descriptionId = computed(() => `${computedId.value}-desc`);
const errorId = computed(() => `${computedId.value}-error`);

const isRequired = computed(
  () => !props.optional && props.validations.includes("notBlank")
);

const { input, dirty, errors, isValid, validate, reset } = useValidation({
  modelValue: model,
  validations: toRef(() => props.validations),
  optional: toRef(() => props.optional),
});

const { isTabbing } = useTabbing();

const focused = ref(false);
const focusedViaKeyboard = ref(false);
const textbox = ref<HTMLInputElement | HTMLTextAreaElement>();
const root = ref<HTMLDivElement>();
const textAreaHeight = ref<number>(0);

const computedWrapperClasses = computed<any>(() => {
  if (props.wrapperClasses) return props.wrapperClasses;

  return "relative full-width";
});

const inputType = computed<InputTypeHTMLAttribute>(() =>
  props.type === "textarea" ? "text" : props.type
);

function focus() {
  focused.value = true;
  textbox.value?.focus();
}

function blur() {
  focused.value = false;
  textbox.value?.blur();
}

function clear() {
  input.value = "";
  emit("clear");
  // Focus after the clear button unmounts (canClear flips false); focusing
  // synchronously is undone when the still-focused button is removed.
  nextTick(focus);
}

function submit(event: KeyboardEvent): void {
  emit("submit", { event, value: input.value ?? null });
}

const computedStyle = computed<CSSProperties>(() => {
  const items: CSSProperties = {};

  if (props.maxHeight) {
    items.maxHeight = `${props.maxHeight}px`;
  }

  if (props.minHeight) {
    items.minHeight = `${props.minHeight}px`;
  }

  if (props.autoGrow && props.type === "textarea") {
    if (model.value && textAreaHeight.value) {
      items.height = `${textAreaHeight.value}px`;
    } else {
      items.height = props.minHeight ? `${props.minHeight}px` : "auto";
      items.overflow = "hidden";
    }
  } else if (props.height) {
    items.height = `${props.height}px`;
  }

  if (props.resize && props.type === "textarea") {
    items.resize = props.resize;
  }

  return items;
});

const handleInput = computed(() => {
  const emitValue = (event: Event) => {
    const target = event.target as HTMLInputElement;

    if (props.type === "file") {
      (model.value as FileList | null) = target.files;
    } else {
      (model.value as string) = target.value;
    }
  };

  if (props.debounce) {
    return lodashDebounce(emitValue, Number(props.debounce));
  }

  return emitValue;
});

const inputMode = computed<InputMode>(() => {
  let result: InputMode;

  if (props.numbersOnly) {
    result = "numeric";
  }

  return result;
});

const eventListeners = computed(() => ({
  onFocus() {
    focused.value = true;
    focusedViaKeyboard.value = isTabbing.value;
    emit("focus");
  },
  onBlur() {
    focused.value = false;
    focusedViaKeyboard.value = false;
    emit("blur");
  },
  onKeypress(event: KeyboardEvent) {
    if (props.numbersOnly) {
      inputNumberOnly(event);
    }
  },
  onKeydown(event: KeyboardEvent) {
    if (["Tab", "Enter"].includes(event.key)) submit(event);

    if (event.key === "Escape") {
      blur();
      emit("blur");
    }

    emit("keydown", event);
  },
  onInput: handleInput.value,
}));

const inputTestId = computed(() => {
  const testId = attrs["data-testid"];

  return typeof testId === "string" ? testId : "input-text";
});

const hasLabelSlot = computed<boolean>(() => Boolean(slots.label));

const inheritedAttributes = computed(() => {
  const attrDescribedBy = attrs["aria-describedby"];
  const attrInvalid = attrs["aria-invalid"];
  const attrRequired = attrs["aria-required"];

  const externalDescribedBy =
    typeof attrDescribedBy === "string" ? attrDescribedBy : undefined;

  const externalInvalid = attrInvalid === true || attrInvalid === "true";
  const externalRequired = attrRequired === true || attrRequired === "true";

  const describedByParts: string[] = [];

  if (props.description && !hasLabelSlot.value) {
    describedByParts.push(descriptionId.value);
  }

  if (errors.value.length) {
    describedByParts.push(errorId.value);
  }

  if (externalDescribedBy) {
    describedByParts.push(externalDescribedBy);
  }

  const externalLabelledBy =
    typeof props.ariaLabelledby === "string" ? props.ariaLabelledby : undefined;

  const labelledByParts = [
    ...(props.label || hasLabelSlot.value ? [labelId.value] : []),
    ...(externalLabelledBy ? [externalLabelledBy] : []),
  ];

  return {
    "aria-describedby": describedByParts.length
      ? describedByParts.join(" ")
      : undefined,
    "aria-invalid": errors.value.length || externalInvalid ? true : undefined,
    "aria-labelledby": labelledByParts.length
      ? labelledByParts.join(" ")
      : undefined,
    "aria-required": isRequired.value || externalRequired ? true : undefined,
    required: isRequired.value || undefined,
    "data-cy": inputTestId.value,
    "data-testid": inputTestId.value,
    ...omit(attrs, [
      "aria-describedby",
      "aria-invalid",
      "aria-labelledby",
      "aria-required",
      "class",
      "data-cy",
      "data-testid",
      "style",
    ]),
  };
});

const hasPrependSlot = ref<boolean>(false);

const hasAppendSlot = ref<boolean>(false);

const canClear = computed(() => props.clearable && input.value);

function updateSlotVisibility() {
  hasPrependSlot.value = hasSlotContent(slots.prepend);
  hasAppendSlot.value = hasSlotContent(slots.append);
}

watch(
  model,
  () => {
    if (props.autoGrow && props.type === "textarea") {
      textAreaHeight.value = 0;

      nextTick(() => {
        if (!textbox.value) return;
        textAreaHeight.value = textbox.value.scrollHeight;
      });
    }
  },
  { immediate: true }
);

onBeforeMount(() => {
  updateSlotVisibility();
});

onBeforeUpdate(() => {
  updateSlotVisibility();
});

onMounted(() => {
  if (props.autoFocus) {
    focus();
  }
});

defineExpose({
  isValid,
  validate,
  reset,
  focus,
  blur,
  el: root,
  input: textbox,
  errors,
  dirty,
});
</script>

<style lang="scss" scoped>
input:placeholder-shown {
  text-overflow: ellipsis;
}

.clear .clear-icon {
  transition-property: background-color, border-color, color;

  &:focus-visible {
    outline-offset: -2px;
  }
}

.input-container:not(.new-design) {
  > .input-wrapper {
    display: flex;
    align-items: center;
    background: $silver-white;
    padding: 0;

    &.not-filled {
      background: transparent;
    }

    &.prepend {
      padding-left: 16px;
    }

    &.rounded {
      border-radius: 6px;
    }

    &.dense {
      &.prepend {
        padding-left: 4px;
      }

      &.append {
        padding-right: 4px;
      }
    }

    > .relative {
      label,
      input {
        font-size: 14px;
        line-height: 18px;
      }

      input {
        width: 100%;
        height: $text-input-height;
        color: $ds-denim-9;
      }

      input[type="number"],
      input[type="number"]::-webkit-inner-spin-button {
        appearance: none;
        appearance: textfield;
        border: none;
      }

      textarea {
        width: 100%;
      }
    }
  }
}
</style>
